// Utility functions for tracking lesson completion
import {
  getAccountScopedLocalStorageKey,
  setLocalStorageWithSync,
  syncLocalStorageToBackend,
} from './utils';
import {
  loadJourneyProgress,
  saveJourneyProgress,
  calculateDayProgress,
} from './languageJourneyData';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
// Some flows persist the user email in `localStorage.userEmail` even when the
// sessionStorage user object isn't available (e.g., after refresh / new tab).
// Journey updates should still work in that case.
const getStoredUserEmail = () => {
  // Prefer sessionStorage user (most reliable for auth-scoped state)
  try {
    const sessionUser = JSON.parse(sessionStorage.getItem('user') || '{}');
    if (sessionUser?.email) return String(sessionUser.email).trim().toLowerCase();
  } catch {
    // ignore
  }

  // Fallback: localStorage userEmail
  const localEmail = localStorage.getItem('userEmail');
  if (localEmail) return String(localEmail).trim().toLowerCase();

  // Fallback: localStorage user blob (used by some modules)
  try {
    const localUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (localUser?.email) return String(localUser.email).trim().toLowerCase();
  } catch {
    // ignore
  }

  return null;
};

const getLessonCompletionStorageKey = () =>
  getAccountScopedLocalStorageKey('lesson_completion_data', getStoredUserEmail());

const getStoredLessonCompletions = () => {
  const storageKey = getLessonCompletionStorageKey();
  if (!storageKey) return {};
  return JSON.parse(localStorage.getItem(storageKey) || '{}');
};

const setStoredLessonCompletions = async (completedLessons) => {
  const storageKey = getLessonCompletionStorageKey();
  if (!storageKey) return;
  await setLocalStorageWithSync(storageKey, JSON.stringify(completedLessons), true);
};

export const markLessonCompleted = async (
  lessonType,
  lessonId,
  journeyActivityId = null
) => {
  const completedLessons = getStoredLessonCompletions();
  const lessonKey = `${lessonType}-${lessonId}`;

  const isFirstTimeCompletion = !completedLessons[lessonKey];

  if (isFirstTimeCompletion) {
    completedLessons[lessonKey] = {
      completedAt: new Date().toISOString(),
      date: 'Today',
    };

    await setStoredLessonCompletions(completedLessons);

    // Dispatch a custom event to notify other components
    window.dispatchEvent(
      new CustomEvent('lessonCompleted', {
        detail: { lessonType, lessonId, lessonKey, journeyActivityId },
      })
    );
  }

  // Always attempt to update the journey + backend, even if the lesson completion
  // key already exists (e.g. merged from backend, or a prior attempt was interrupted).
  // This is idempotent because journey activities are only marked if not completed.
  try {
    await updateJourneyActivityCompletion(lessonType, lessonId, journeyActivityId);
  } catch (error) {
    console.error('Error updating journey activity completion:', error);
  }

  // Force immediate sync to backend (so `/configure` is triggered even if the
  // completion key already existed).
  try {
    const userEmail = getStoredUserEmail();
    if (userEmail) {
      console.log('Force syncing lesson completion to backend...');
      await syncLocalStorageToBackend(userEmail, true);
    }
  } catch (error) {
    console.error('Error syncing lesson completion:', error);
  }
};

/**
 * Update journey activity completion when a lesson/speaking is completed
 * @param {string} activityType - Type of activity ('grammar', 'vocabulary', 'speaking', etc.)
 * @param {string} lessonId - The lesson ID that was completed
 * @param {string} journeyActivityId - The specific journey activity ID (e.g., "listening-test-week-1")
 */
const updateJourneyActivityCompletion = async (
  activityType,
  lessonId,
  journeyActivityId = null
) => {
  try {
    const userEmail = getStoredUserEmail();
    if (!userEmail) return;

    const journeyData = loadJourneyProgress(userEmail);
    if (!journeyData?.journey || !journeyData.enrolled) return;

    // Helper: normalize string for comparison (remove special chars, lowercase)
    const normalize = (str) => str?.toLowerCase().replace(/[-_\s]/g, '') || '';

    // Helper: check if lesson ID matches an activity (for precise day matching)
    const isLessonMatch = (activity, lessonId) => {
      const normalizedLessonId = normalize(lessonId);
      return (
        // Direct lessonId match (best - stored in activity)
        activity.lessonId === lessonId ||
        normalize(activity.lessonId) === normalizedLessonId ||
        // Component matching (e.g., 'VocabularyForWriting' matches 'vocabulary-writing')
        normalize(activity.component) === normalizedLessonId ||
        // Path contains the lessonId
        activity.path?.toLowerCase().includes(lessonId.toLowerCase())
      );
    };

    let targetDayIndex = -1;
    let targetActivity = null;

    // PASS 0: If we have a specific journeyActivityId, find that exact activity first
    if (journeyActivityId) {
      for (let i = 0; i < journeyData.journey.length; i++) {
        const day = journeyData.journey[i];
        const activity = day.activities.find(
          (a) => a.id === journeyActivityId && !a.completed
        );
        if (activity) {
          targetDayIndex = i;
          targetActivity = activity;
          console.log(
            `✅ Found activity by ID "${journeyActivityId}" on Day ${i + 1}`
          );
          break;
        }
      }
    }

    // For grammar, vocabulary, and course lessons:
    // Search ALL days (past, current, future) for the EXACT lesson match
    // This ensures the correct day gets marked regardless of its status
    const isLessonType =
      activityType === 'grammar' ||
      activityType === 'vocabulary' ||
      activityType.endsWith('-course');

    if (targetDayIndex === -1 && isLessonType) {
      // Search through ALL days for the specific lesson
      for (let i = 0; i < journeyData.journey.length; i++) {
        const day = journeyData.journey[i];
        for (const activity of day.activities) {
          if (activity.completed) continue;
          if (
            activity.type === activityType &&
            isLessonMatch(activity, lessonId)
          ) {
            targetDayIndex = i;
            targetActivity = activity;
            console.log(
              `✅ Found lesson "${lessonId}" on Day ${i + 1} (status: ${day.status})`
            );
            break;
          }
        }
        if (targetActivity) break;
      }
    }
    // For test types (reading, writing, listening on checkpoint days):
    // Only search on checkpoint days for tests
    else if (
      targetDayIndex === -1 &&
      (activityType === 'reading' ||
        activityType === 'writing' ||
        activityType === 'listening')
    ) {
      for (let i = 0; i < journeyData.journey.length; i++) {
        const day = journeyData.journey[i];
        // Only search on checkpoint days for tests
        if (!day.isCheckpoint) continue;
        for (const activity of day.activities) {
          if (activity.completed) continue;
          if (activity.type === activityType && activity.isTest) {
            targetDayIndex = i;
            targetActivity = activity;
            console.log(
              `✅ Found ${activityType} test on Day ${i + 1} (checkpoint)`
            );
            break;
          }
        }
        if (targetActivity) break;
      }
    }

    if (targetDayIndex === -1 || !targetActivity) {
      console.log(
        `⚠️ No matching activity found for ${activityType}:${lessonId}`
      );
      return;
    }

    // Mark the activity as completed
    targetActivity.completed = true;
    targetActivity.completedAt = new Date().toISOString();

    console.log(
      `✅ Journey: Marked ${activityType} activity as completed on Day ${targetDayIndex + 1} (matched: ${lessonId})`
    );

    // Recalculate day progress
    const targetDay = journeyData.journey[targetDayIndex];
    const dayProgress = calculateDayProgress(targetDay);
    targetDay.progress = dayProgress;
    targetDay.status = dayProgress.status;

    // Save updated journey
    await saveJourneyProgress(userEmail, journeyData);

    console.log(
      `✅ Journey Day ${targetDayIndex + 1} progress: ${dayProgress.percentage}% (${dayProgress.status})`
    );

    // Dispatch event to notify components
    window.dispatchEvent(
      new CustomEvent('journeyProgressUpdated', {
        detail: { dayIndex: targetDayIndex, progress: dayProgress },
      })
    );
  } catch (error) {
    console.error('Error updating journey progress:', error);
  }
};

export const isLessonCompleted = (lessonType, lessonId) => {
  const completedLessons = getStoredLessonCompletions();
  const lessonKey = `${lessonType}-${lessonId}`;
  return !!completedLessons[lessonKey];
};

export const getCompletedLessons = () => {
  return getStoredLessonCompletions();
};

export const removeLessonCompletion = async (lessonType, lessonId) => {
  const completedLessons = getStoredLessonCompletions();
  const lessonKey = `${lessonType}-${lessonId}`;

  if (completedLessons[lessonKey]) {
    delete completedLessons[lessonKey];

    await setStoredLessonCompletions(completedLessons);

    // Force immediate sync to backend
    try {
      const user = JSON.parse(sessionStorage.getItem('user') || '{}');
      if (user.email) {
        await syncLocalStorageToBackend(user.email, true);
      }
    } catch (error) {
      console.error('Error syncing lesson removal:', error);
    }

    // Dispatch a custom event to notify other components
    window.dispatchEvent(
      new CustomEvent('lessonRemoved', {
        detail: { lessonType, lessonId, lessonKey },
      })
    );
  }
};

/**
 * Mark speaking session as completed in the journey
 * @param {string} topicCategory - The speaking topic category
 * @param {string} topic - The specific speaking topic (for precise matching)
 * @param {string} topicId - The topic ID (for precise matching)
 * @param {boolean} isIELTSTest - Whether this is an IELTS speaking test
 * @param {object} testResults - Optional test results with scores and assessment data
 * @param {string} journeyActivityId - The specific journey activity ID (e.g., "speaking-test-week-1")
 */
export const markSpeakingSessionCompleted = async (
  topicCategory = null,
  topic = null,
  topicId = null,
  isIELTSTest = false,
  testResults = null,
  journeyActivityId = null
) => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user?.email) return;

    const journeyData = loadJourneyProgress(user.email);
    if (!journeyData?.journey || !journeyData.enrolled) return;

    let targetDayIndex = -1;
    let speakingActivity = null;

    // PASS 0: If we have a specific journeyActivityId, find that exact activity first
    if (journeyActivityId) {
      for (let i = 0; i < journeyData.journey.length; i++) {
        const day = journeyData.journey[i];
        const activity = day.activities.find(
          (a) => a.id === journeyActivityId && !a.completed
        );
        if (activity) {
          targetDayIndex = i;
          speakingActivity = activity;
          console.log(
            `✅ Found speaking activity by ID "${journeyActivityId}" on Day ${i + 1}`
          );
          break;
        }
      }
    }

    // PASS 1: If IELTS test and no specific ID match, search for the FIRST uncompleted speaking test on checkpoint days
    if (targetDayIndex === -1 && isIELTSTest) {
      for (let i = 0; i < journeyData.journey.length; i++) {
        const day = journeyData.journey[i];
        // Only search on checkpoint days for IELTS tests
        if (!day.isCheckpoint) continue;
        const activity = day.activities.find(
          (a) => a.type === 'speaking' && a.isTest && !a.completed
        );
        if (activity) {
          targetDayIndex = i;
          speakingActivity = activity;
          console.log(
            `✅ Found IELTS speaking test on Day ${i + 1} (checkpoint)`
          );
          break;
        }
      }
    }

    // If found an IELTS test, mark and save immediately
    if (
      targetDayIndex !== -1 &&
      speakingActivity &&
      (isIELTSTest || speakingActivity.isTest)
    ) {
      // Mark and save immediately
      speakingActivity.completed = true;
      speakingActivity.completedAt = new Date().toISOString();

      // Save test results if provided
      if (testResults) {
        speakingActivity.score = testResults.overallBandScore || null;
        speakingActivity.testResults = {
          overallBandScore: testResults.overallBandScore,
          fluencyAndCoherence: testResults.fluencyAndCoherence,
          lexicalResource: testResults.lexicalResource,
          grammaticalRangeAndAccuracy: testResults.grammaticalRangeAndAccuracy,
          pronunciation: testResults.pronunciation,
          cefrLevel: testResults.cefrLevel,
          testId: testResults.testId,
          ieltsTestData: testResults.ieltsTestData,
          noResponses: testResults.noResponses || false, // Track if user didn't speak
        };
        speakingActivity.retakeCount = 0; // First attempt
        speakingActivity.canRetake = true; // Can retake once
        speakingActivity.hadNoResponses = testResults.noResponses || false; // Track for free retake
      }

      const targetDay = journeyData.journey[targetDayIndex];
      const dayProgress = calculateDayProgress(targetDay);
      targetDay.progress = dayProgress;
      targetDay.status = dayProgress.status;

      await saveJourneyProgress(user.email, journeyData);

      window.dispatchEvent(
        new CustomEvent('journeyProgressUpdated', {
          detail: { dayIndex: targetDayIndex, progress: dayProgress },
        })
      );

      console.log(
        `✅ IELTS speaking test marked complete on Day ${targetDayIndex + 1}. Score: ${testResults?.overallBandScore || 'N/A'}. Day progress: ${dayProgress.percentage}%`
      );
      return;
    }

    // PASS 1: Search ALL days for exact topic/topicId match (most precise)
    if (topic || topicId) {
      for (let i = 0; i < journeyData.journey.length; i++) {
        const day = journeyData.journey[i];
        const activity = day.activities.find(
          (a) => a.type === 'speaking' && !a.completed
        );

        if (activity) {
          // Match by topicId (most precise)
          if (topicId && activity.topicId === topicId) {
            targetDayIndex = i;
            speakingActivity = activity;
            console.log(`✅ Found speaking topic by ID on Day ${i + 1}`);
            break;
          }
          // Match by topic text
          if (
            topic &&
            (activity.topic?.toLowerCase() === topic.toLowerCase() ||
              day.speakingTopic?.toLowerCase() === topic.toLowerCase())
          ) {
            targetDayIndex = i;
            speakingActivity = activity;
            console.log(`✅ Found speaking topic by text on Day ${i + 1}`);
            break;
          }
        }
      }
    }

    // PASS 2: Search ALL days for category match
    if (targetDayIndex === -1 && topicCategory) {
      for (let i = 0; i < journeyData.journey.length; i++) {
        const day = journeyData.journey[i];
        const activity = day.activities.find(
          (a) => a.type === 'speaking' && !a.completed
        );

        if (activity) {
          if (
            activity.category?.toLowerCase() === topicCategory.toLowerCase() ||
            day.speakingCategory?.toLowerCase() === topicCategory.toLowerCase()
          ) {
            targetDayIndex = i;
            speakingActivity = activity;
            console.log(`✅ Found speaking by category on Day ${i + 1}`);
            break;
          }
        }
      }
    }

    // PASS 3: Fallback - find first uncompleted speaking activity on ANY day
    if (targetDayIndex === -1) {
      for (let i = 0; i < journeyData.journey.length; i++) {
        const day = journeyData.journey[i];
        const activity = day.activities.find(
          (a) => a.type === 'speaking' && !a.completed
        );
        if (activity) {
          targetDayIndex = i;
          speakingActivity = activity;
          console.log(`📝 Fallback: marking speaking on Day ${i + 1}`);
          break;
        }
      }
    }

    if (targetDayIndex === -1 || !speakingActivity) {
      console.log('⚠️ No uncompleted speaking activity found');
      return;
    }

    // Mark speaking activity as completed
    speakingActivity.completed = true;
    speakingActivity.completedAt = new Date().toISOString();

    // Save test results if provided (for regular speaking practice)
    if (testResults) {
      speakingActivity.score = testResults.overallBandScore || null;
      speakingActivity.testResults = {
        overallBandScore: testResults.overallBandScore,
        fluencyAndCoherence: testResults.fluencyAndCoherence,
        lexicalResource: testResults.lexicalResource,
        grammaticalRangeAndAccuracy: testResults.grammaticalRangeAndAccuracy,
        pronunciation: testResults.pronunciation,
        cefrLevel: testResults.cefrLevel,
        testId: testResults.testId,
      };
    }

    const targetDay = journeyData.journey[targetDayIndex];

    // Recalculate day progress
    const dayProgress = calculateDayProgress(targetDay);
    targetDay.progress = dayProgress;
    targetDay.status = dayProgress.status;

    // Save updated journey
    await saveJourneyProgress(user.email, journeyData);

    // Dispatch event to notify components
    window.dispatchEvent(
      new CustomEvent('journeyProgressUpdated', {
        detail: { dayIndex: targetDayIndex, progress: dayProgress },
      })
    );

    console.log(
      `✅ Speaking session marked complete on Day ${targetDayIndex + 1}. Score: ${testResults?.overallBandScore || 'N/A'}. Day progress: ${dayProgress.percentage}%`
    );
  } catch (error) {
    console.error('Error marking speaking session complete:', error);
  }
};

/**
 * Get retake information for an IELTS speaking test
 * @param {number} dayNumber - The day number in the journey (1-indexed)
 * @returns {object} - Retake info: { canRetake, retakeCount, testResults }
 */
export const getSpeakingTestRetakeInfo = (dayNumber = null) => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user?.email)
      return { canRetake: false, retakeCount: 0, testResults: null };

    const journeyData = loadJourneyProgress(user.email);
    if (!journeyData?.journey || !journeyData.enrolled) {
      return { canRetake: false, retakeCount: 0, testResults: null };
    }

    // Find the speaking test activity
    let speakingActivity = null;
    let foundDayIndex = -1;

    if (dayNumber) {
      // Find specific day
      const dayIndex = dayNumber - 1;
      if (dayIndex >= 0 && dayIndex < journeyData.journey.length) {
        const day = journeyData.journey[dayIndex];
        speakingActivity = day.activities.find(
          (a) => a.type === 'speaking' && a.isTest && a.completed
        );
        foundDayIndex = dayIndex;
      }
    } else {
      // Find the most recent completed speaking test
      for (let i = journeyData.journey.length - 1; i >= 0; i--) {
        const day = journeyData.journey[i];
        const activity = day.activities.find(
          (a) => a.type === 'speaking' && a.isTest && a.completed
        );
        if (activity) {
          speakingActivity = activity;
          foundDayIndex = i;
          break;
        }
      }
    }

    if (!speakingActivity) {
      return {
        canRetake: false,
        retakeCount: 0,
        testResults: null,
        dayIndex: -1,
      };
    }

    const retakeCount = speakingActivity.retakeCount || 0;
    const hadNoResponses =
      speakingActivity.hadNoResponses ||
      speakingActivity.testResults?.noResponses ||
      false;
    const canRetake = retakeCount < 1; // Can only retake once

    return {
      canRetake,
      retakeCount,
      testResults: speakingActivity.testResults || null,
      score: speakingActivity.score,
      dayIndex: foundDayIndex,
      dayNumber: foundDayIndex + 1,
      hadNoResponses, // True if first attempt had no user responses
      isFreeRetake: hadNoResponses && retakeCount === 0, // Free retake if first attempt had no responses
    };
  } catch (error) {
    console.error('Error getting speaking test retake info:', error);
    return { canRetake: false, retakeCount: 0, testResults: null };
  }
};

/**
 * Update the speaking test after a retake
 * @param {number} dayNumber - The day number in the journey (1-indexed)
 * @param {object} newTestResults - The new test results
 * @returns {boolean} - Whether the update was successful
 */
export const updateSpeakingTestRetake = async (dayNumber, newTestResults) => {
  try {
    const user = JSON.parse(sessionStorage.getItem('user'));
    if (!user?.email) return false;

    const journeyData = loadJourneyProgress(user.email);
    if (!journeyData?.journey || !journeyData.enrolled) return false;

    const dayIndex = dayNumber - 1;
    if (dayIndex < 0 || dayIndex >= journeyData.journey.length) return false;

    const day = journeyData.journey[dayIndex];
    const speakingActivity = day.activities.find(
      (a) => a.type === 'speaking' && a.isTest
    );

    if (!speakingActivity) return false;

    // Check if retake is allowed
    const currentRetakeCount = speakingActivity.retakeCount || 0;
    if (currentRetakeCount >= 1) {
      console.log('⚠️ Maximum retakes reached for this speaking test');
      return false;
    }

    // Update the activity with new results
    speakingActivity.retakeCount = currentRetakeCount + 1;
    speakingActivity.canRetake = false; // No more retakes allowed
    speakingActivity.lastRetakeAt = new Date().toISOString();

    // Save new test results (keep previous results for comparison)
    speakingActivity.previousTestResults = speakingActivity.testResults;
    speakingActivity.testResults = {
      overallBandScore: newTestResults.overallBandScore,
      fluencyAndCoherence: newTestResults.fluencyAndCoherence,
      lexicalResource: newTestResults.lexicalResource,
      grammaticalRangeAndAccuracy: newTestResults.grammaticalRangeAndAccuracy,
      pronunciation: newTestResults.pronunciation,
      cefrLevel: newTestResults.cefrLevel,
      testId: newTestResults.testId,
      ieltsTestData: newTestResults.ieltsTestData,
      isRetake: true,
    };

    // Update score to the better of the two
    const previousScore =
      speakingActivity.previousTestResults?.overallBandScore || 0;
    const newScore = newTestResults.overallBandScore || 0;
    speakingActivity.score = Math.max(previousScore, newScore);

    // Save updated journey
    await saveJourneyProgress(user.email, journeyData);

    // Dispatch event
    window.dispatchEvent(
      new CustomEvent('speakingTestRetakeCompleted', {
        detail: {
          dayNumber,
          newScore,
          previousScore,
          finalScore: speakingActivity.score,
        },
      })
    );

    console.log(
      `✅ Speaking test retake saved for Day ${dayNumber}. Previous: ${previousScore}, New: ${newScore}, Final: ${speakingActivity.score}`
    );
    return true;
  } catch (error) {
    console.error('Error updating speaking test retake:', error);
    return false;
  }
};

/**
 * Mark the current day's revision session as completed
 * @param {string} userEmail
 * @returns {boolean} success
 */
export const markRevisionSessionCompleted = async (userEmail) => {
  try {
    const journeyData = loadJourneyProgress(userEmail);
    if (!journeyData?.journey) return false;

    const currentDay = journeyData.currentDay || 1;
    const dayPlan = journeyData.journey.find((d) => d.day === currentDay);
    if (!dayPlan) return false;

    const revisionActivity = dayPlan.activities?.find(
      (a) => a.type === 'revision-session' || a.type === 'revision-quiz'
    );
    if (revisionActivity) {
      revisionActivity.completed = true;
      revisionActivity.completedAt = new Date().toISOString();
    }

    await saveJourneyProgress(userEmail, journeyData);
    return true;
  } catch (error) {
    console.error('Error marking revision session completed:', error);
    return false;
  }
};
